// Type imports
import type React from 'react'
import type { NextPage, GetStaticProps } from 'next'
import type { LinkTypes, I18nextKey, ActionTypes } from '../../types'
import type { RequiredNs as ComponentNs } from '../../components/localized/types'

// External imports
import Image from '../../components/NextImage'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation, Trans } from 'react-i18next'

import styled from '@emotion/styled'

// Other
import useScrollPassed from '../../hooks/use-scrolled-passed'

import Header from '../../components/localized/Header'
import Footer from '../../components/localized/Footer'
import PlatformLogos from '../../components/localized/PlatformLogos'
import ChromeStoreRating from '../../components/localized/ChromeStoreRating'
import Testimonials from '../../components/localized/Testimonials'

import {
  FlexHeroSection,
  FlexItem as HeroFlexItem,
  Title as HeroTitle,
  Description as HeroDescription
} from '../../components/Hero'
import PrimaryAction from '../../components/PrimaryAction'
import { FlexContainer, Item } from '../../components/Section'
import { CenteredSection, Title as CenteredTitle, SpacingContainer } from '../../components/CenteredSection'

import ColoredBackground from '../../components/ColoredBackground'
import Highlight from '../../components/Highlight'
import FeatureGrid, { FeatureBlock } from '../../components/FeatureGrid'
import OrderedList from '../../components/OrderedList'

import BottomCta from '../../components/BottomCta'

import RewriteCasualImage from '../../../public/assets/feature-images/en/rewrite-casual-hero.png'
import RewriteHowToImage from '../../../public/assets/feature-images/en/rewrite-tutorial.svg'

import { DEFAULT } from '../../font'
import {
  MOBILE_WIDTH_PX,
  defaultPageSpecificSEO,
  pageSpecificCtaLink
} from '../../constants'

/**
 * Load all the namespaces required for this page include all localized components
 */
type RequiredNs = 'casual' | 'common' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, casual: true }
export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? '', Object.keys(namespaces)))
  }
})

/**
 * Constants & Styles
 */

const HERO_BACKGROUND = 'purple'

const PaddedLinkButton = styled(PrimaryAction)`
  margin-top: 32px;
`

const StyledFlexContainer = styled(FlexContainer)`
  text-align: left;
  ${DEFAULT[600]}
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    ${DEFAULT[200]}
  }
`

const StyledOrderedList = styled(OrderedList)`
  padding: 0;
`

/**
 * Types for structure of page
 */

// did this manually for each key because it was a pain to sort out typescript issues
// but it would be better if we just type it so its just the prefix of the key needed
type CasualKey = I18nextKey<typeof import ('../../../public/locales/en/casual.json')>

type FeatureBlockContent = (
  Record<'titleKey' | 'descriptionKey', CasualKey> &
  Omit<FeatureBlock, 'title' | 'description'> &
  Partial<LinkTypes> // IDK why but typescript does not find the 'linkTypes' in FeatureBlock?
)

/**
 * Mapping json keys for content structure
 */

const FEATURES: FeatureBlockContent[] = [
  {
    titleKey: 'features.authentic.TITLE',
    descriptionKey: 'features.authentic.DESCRIPTION',
    icon: { src: require('../../../public/assets/icons/thumb-up.svg'), alt: '' },
    bgColor: 'yellow'
  },
  {
    titleKey: 'features.tone.TITLE',
    descriptionKey: 'features.tone.DESCRIPTION',
    icon: { src: require('../../../public/assets/icons/insert-emoticon.svg'), alt: '' },
    bgColor: 'purple'
  },
  {
    titleKey: 'features.readability.TITLE',
    descriptionKey: 'features.readability.DESCRIPTION',
    icon: { src: require('../../../public/assets/icons/open-book.svg'), alt: '' },
    bgColor: 'red'
  }
]

const ADV_FEATURES: FeatureBlockContent[] = [
  {
    href: '/paraphrasing/formal',
    titleKey: 'advfeatures.formal.TITLE',
    descriptionKey: 'advfeatures.formal.DESCRIPTION',
    icon: { src: require('../../../public/assets/icons/formal.svg'), alt: '' }
  },
  {
    href: '/paraphrasing/shorten',
    titleKey: 'advfeatures.shorten.TITLE',
    descriptionKey: 'advfeatures.shorten.DESCRIPTION',
    icon: { src: require('../../../public/assets/icons/shorten.svg'), alt: '' }
  },
  {
    href: '/paraphrasing/restructure',
    titleKey: 'advfeatures.restructure.TITLE',
    descriptionKey: 'advfeatures.restructure.DESCRIPTION',
    icon: { src: require('../../../public/assets/icons/shuffle.svg'), alt: '' }
  }
]

/**
 * Page Component
 */

const Casual: NextPage = () => {
  const router = useRouter()

  const { t } = useTranslation('casual')
  const { t: tCommon } = useTranslation('common')

  const ctaAction: ActionTypes = { ...t('CTA_BUTTON'), ...pageSpecificCtaLink(router.pathname, router.locale) }

  // Controls for the header to only go sticky after user passes the fold
  const { ref: heroCtaRef, scrolledPassed } = useScrollPassed()

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />

      {/* Above the fold */}
      <Header
        t={tCommon}
        action={ctaAction}
        data-sticky={scrolledPassed}
        bgColor={HERO_BACKGROUND}
        dark
      />
      <ColoredBackground bgColor={HERO_BACKGROUND}>
        {/* Hero */}
        <FlexHeroSection>
          <HeroFlexItem>
            <HeroTitle>{t('hero.TITLE')}</HeroTitle>
            <HeroDescription>{t('hero.DESCRIPTION')}</HeroDescription>
            <div>
              <PrimaryAction {...ctaAction} ref={heroCtaRef} clickId='hero-cta' />
              <ChromeStoreRating t={tCommon} />
            </div>
          </HeroFlexItem>
          <HeroFlexItem>
            <Image src={RewriteCasualImage} alt={t('hero.REWRITE_CASUAL_IMG_ALT')} priority />
          </HeroFlexItem>
        </FlexHeroSection>
      </ColoredBackground>

      <CenteredSection bgColor='gray'>
        {/* Goals features grid */}
        <SpacingContainer>
          <CenteredTitle>
            <Trans t={t} i18nKey='features.TITLE'><Highlight color='yellow'/></Trans>
          </CenteredTitle>
          <FeatureGrid
            list={FEATURES.map(({ titleKey, descriptionKey, ...rest }) => ({
              ...rest,
              title: t(titleKey) as string,
              description: t(descriptionKey) as string
            }))}
          />
        </SpacingContainer>

        {/* Tutorial */}
        <SpacingContainer>
          <CenteredTitle>
            <Trans t={t} i18nKey='tutorial.TITLE'><Highlight color='red'/></Trans>
          </CenteredTitle>
        </SpacingContainer>
        <SpacingContainer>
          <StyledFlexContainer>
            <Item><Image src={RewriteHowToImage} alt='' /></Item>
            <Item>
              <StyledOrderedList
                list={[
                  t('tutorial.steps.HIGHLIGHT'),
                  t('tutorial.steps.SELECT_GOAL'),
                  t('tutorial.steps.REWRITE')
                ]}
              />
            </Item>
          </StyledFlexContainer>
        </SpacingContainer>
      </CenteredSection>

      {/* Platforms */}
      <CenteredSection>
        <CenteredTitle>
          <Trans t={t} i18nKey='platforms.TITLE'><Highlight color='yellow'/></Trans>
        </CenteredTitle>
        <PlatformLogos t={tCommon} platforms={['google-docs', 'gmail', 'outlook', 'facebook', 'linkedin', 'wordpress']} />
        <PaddedLinkButton
          text={t('platforms.ACTION_TEXT')}
          {...pageSpecificCtaLink('platform', router.locale)}
          clickId='add-to-chrome'
        />
      </CenteredSection>

      {/* Adv features */}
      <CenteredSection bgColor='gray'>
        <CenteredTitle>
          <Trans t={t} i18nKey='advfeatures.TITLE'><Highlight color='purple'/></Trans>
        </CenteredTitle>
        <FeatureGrid
          list={ADV_FEATURES.map(({ titleKey, descriptionKey, ...rest }) => ({
            ...rest,
            title: t(titleKey) as string,
            description: t(descriptionKey) as string
          }))}
        />
      </CenteredSection>

      {/* Testimonials */}
      <Testimonials t={tCommon} description={t('testimonials.DESCRIPTION')} />

      {/* BottomCta */}
      <BottomCta action={ctaAction} {...t('BOTTOM_CTA')} />

      {/* Footer */}
      <Footer t={tCommon} />
    </>
  )
}

export default Casual
